import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { dateOnly, money, percent } from "../lib/format";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Layout";
import { Card, DataState, Notice, StatTile, Table } from "../components/ui";

type DailySales = {
  basis: string;
  note: string;
  days: Array<{ date: string; net: number; tax: number; gross: number; count: number }>;
  totals: { net: number; tax: number; gross: number };
};

type JobMargins = {
  rows: Array<{
    id: string;
    number: string;
    customer: string;
    vehicle: string;
    netSales: number;
    partsCost: number;
    laborCost: number;
    directCost: number;
    grossProfit: number;
    grossMarginPct: number | null;
    provisional: boolean;
    missingCosts: boolean;
  }>;
  flaggedCount: number;
  note: string;
};

type TechHours = {
  rows: Array<{
    technicianId: string;
    name: string;
    workedHours: number;
    billedHours: number;
    jobCount: number;
    efficiency: number | null;
  }>;
};

export function ReportsPage() {
  const { can } = useAuth();

  const sales = useQuery({
    queryKey: ["daily-sales"],
    queryFn: () => api.get<DailySales>("/reports/daily-sales"),
    enabled: can("reports.sales"),
  });

  const margins = useQuery({
    queryKey: ["job-margins"],
    queryFn: () => api.get<JobMargins>("/reports/job-margins"),
    enabled: can("reports.costs"),
  });

  const hours = useQuery({
    queryKey: ["technician-hours"],
    queryFn: () => api.get<TechHours>("/reports/technician-hours"),
    enabled: can("reports.costs"),
  });

  return (
    <>
      <PageHeader
        title="Reports"
        description="Last 30 days. Every figure states its date basis and whether costs are final."
      />

      <div className="space-y-5">
        {can("reports.sales") && (
          <DataState loading={sales.isLoading} error={sales.error}>
            {sales.data && (
              <>
                <div className="grid gap-4 sm:grid-cols-3">
                  <StatTile
                    label="Net sales"
                    value={money(sales.data.totals.net)}
                    sub="Excludes sales tax"
                  />
                  <StatTile
                    label="Sales tax collected"
                    value={money(sales.data.totals.tax)}
                    sub="A liability, not revenue"
                  />
                  <StatTile label="Invoiced gross" value={money(sales.data.totals.gross)} />
                </div>

                <Card title="Daily sales" className="mt-4">
                  <div className="mb-3">
                    <Notice>
                      Basis: {sales.data.basis}. {sales.data.note}
                    </Notice>
                  </div>
                  <DataState
                    empty={sales.data.days.length === 0}
                    emptyMessage="No invoices issued in this period."
                  >
                    <Table headers={["Date", "Invoices", "Net sales", "Tax", "Gross"]}>
                      {sales.data.days.map((d) => (
                        <tr key={d.date}>
                          <td className="px-3 py-2">{dateOnly(d.date)}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-600">{d.count}</td>
                          <td className="px-3 py-2 tabular-nums">{money(d.net)}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-600">{money(d.tax)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">
                            {money(d.gross)}
                          </td>
                        </tr>
                      ))}
                    </Table>
                  </DataState>
                </Card>
              </>
            )}
          </DataState>
        )}

        {can("reports.costs") ? (
          <>
            <Card title="Job margins">
              <DataState
                loading={margins.isLoading}
                error={margins.error}
                empty={margins.data?.rows.length === 0}
                emptyMessage="No invoiced jobs in this period."
              >
                {margins.data && margins.data.flaggedCount > 0 && (
                  <div className="mb-3">
                    <Notice tone="warn">
                      {margins.data.flaggedCount} job(s) have provisional or missing costs. Those
                      margins are not final. {margins.data.note}
                    </Notice>
                  </div>
                )}
                <Table
                  headers={["Job", "Customer", "Net sales", "Parts", "Labor", "Gross profit", "Margin", ""]}
                >
                  {margins.data?.rows.map((r) => (
                    <tr key={r.id} className={r.missingCosts ? "bg-amber-50/50" : ""}>
                      <td className="px-3 py-2">
                        <Link to={`/jobs/${r.id}`} className="font-mono text-xs text-teal-700 hover:underline">
                          {r.number}
                        </Link>
                        <div className="text-xs text-slate-500">{r.vehicle}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{r.customer}</td>
                      <td className="px-3 py-2 tabular-nums">{money(r.netSales)}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">
                        {money(r.partsCost)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">
                        {money(r.laborCost)}
                      </td>
                      <td className="px-3 py-2 tabular-nums font-medium">
                        {money(r.grossProfit)}
                      </td>
                      <td className="px-3 py-2 tabular-nums">{percent(r.grossMarginPct)}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {r.missingCosts && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
                            costs incomplete
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              </DataState>
            </Card>

            <Card title="Technician billed versus worked hours">
              <DataState
                loading={hours.isLoading}
                error={hours.error}
                empty={hours.data?.rows.length === 0}
                emptyMessage="No time logged in this period."
              >
                <Table headers={["Technician", "Jobs", "Worked hours", "Billed hours", "Efficiency"]}>
                  {hours.data?.rows.map((r) => (
                    <tr key={r.technicianId}>
                      <td className="px-3 py-2">{r.name}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">{r.jobCount}</td>
                      <td className="px-3 py-2 tabular-nums">{r.workedHours.toFixed(2)}</td>
                      <td className="px-3 py-2 tabular-nums">{r.billedHours.toFixed(2)}</td>
                      <td className="px-3 py-2 tabular-nums font-medium">
                        {percent(r.efficiency)}
                      </td>
                    </tr>
                  ))}
                </Table>
              </DataState>
            </Card>
          </>
        ) : (
          <Notice tone="warn">
            Cost and margin reports are restricted to owners, managers and bookkeepers.
          </Notice>
        )}
      </div>
    </>
  );
}
